import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeft, Save, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import RichTextEditor from '../../../../components/RichTextEditor/RichTextEditor';

export default function FullDescriptionEditor({ 
  formData, 
  onSave, 
  onBack, 
  isBlogMode = false 
}) {
  const [localFormData, setLocalFormData] = useState(formData);
  const navigate = useNavigate();
  const editorRef = useRef(null);

  // Auto-save functionality
  useEffect(() => {
    const autoSaveInterval = setInterval(() => {
      if (localFormData.fullDescription) {
        localStorage.setItem('blog_fullDescription_autosave', localFormData.fullDescription);
      }
    }, 30000); // Auto-save every 30 seconds

    return () => clearInterval(autoSaveInterval);
  }, [localFormData.fullDescription]);

  // Load auto-saved content on mount
  useEffect(() => {
    const autoSaved = localStorage.getItem('blog_fullDescription_autosave');
    if (autoSaved && !localFormData.fullDescription) {
      setLocalFormData(prev => ({ ...prev, fullDescription: autoSaved }));
    }
  }, []);

  const handleContentChange = (content) => {
    setLocalFormData(prev => ({ ...prev, fullDescription: content }));
  };

  const handleSave = () => {
    // Clear auto-save when manually saving
    localStorage.removeItem('blog_fullDescription_autosave');
    onSave(localFormData);
  };

  const handlePreview = () => {
    // Save current content to localStorage for preview
    localStorage.setItem('blog_preview_content', localFormData.fullDescription);
    localStorage.setItem('blog_preview_title', localFormData.title || 'Preview');
    
    // Open preview in new tab
    const previewUrl = '/admin/blog-preview';
    window.open(previewUrl, '_blank');
  };

  return (
    <div className="min-h-screen bg-[#FFFAEB]">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Sections
            </button>
            <div className="h-6 w-px bg-gray-300" />
            <h1 className="text-xl font-semibold" style={{ color: '#105652' }}>
              Full {isBlogMode ? 'Blog' : 'Project'} Description
            </h1>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={handlePreview}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all duration-300 hover:scale-105"
              style={{ borderColor: '#105652', color: '#105652' }}
            >
              <Eye className="w-5 h-5" />
              Preview
            </button>
            <button
              onClick={handleSave}
              className="flex items-center gap-2 px-6 py-2 rounded-lg text-white transition-all duration-300 hover:scale-105"
              style={{ backgroundColor: '#105652' }}
            >
              <Save className="w-5 h-5" />
              Save Section
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex h-[calc(100vh-80px)]">
        {/* Left Sidebar - 20% */}
        <div className="w-1/5 bg-white border-r border-gray-200 p-4 overflow-y-auto">
          <h2 className="text-lg font-semibold mb-4" style={{ color: '#105652' }}>
            Tools for Crafting
          </h2>
          
          {/* This would contain all the toolbar tools from RichTextEditorPage */}
          <div className="space-y-3">
            <div className="text-sm text-gray-600">
              Use the rich text editor on the right to create your content with:
            </div>
            <ul className="text-xs text-gray-500 space-y-1">
              <li>• Text formatting (Bold, Italic, etc.)</li>
              <li>• Headings (H1-H6)</li>
              <li>• Lists and alignment</li>
              <li>• Images and media</li>
              <li>• Code blocks</li>
              <li>• Tables and quotes</li>
              <li>• And much more...</li>
            </ul>
            
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-xs text-green-700">
                💡 <strong>Auto-save:</strong> Your content is automatically saved every 30 seconds.
              </p>
            </div>
          </div>
        </div>

        {/* Right Content - 80% */}
        <div className="flex-1 bg-white">
          <div className="h-full p-6">
            <div className="h-full border rounded-lg" style={{ borderColor: '#105652' }}>
              <RichTextEditor
                ref={editorRef}
                value={localFormData.fullDescription}
                onChange={handleContentChange}
                placeholder={`Start writing your ${isBlogMode ? 'blog' : 'project'} content here...`}
                className="h-full"
                hideToolbar={true} // Hide toolbar since we have it in the sidebar
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}